import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useSnackbar } from "notistack";
import { useDeleteTableIcon, useTableIcons, useUploadTableIcon } from "../../../api/adminTableIcons";
import useAuth from "../../../hooks/useAuth";
import {
  iconConflictReferences,
  iconErrorMessage,
  svgHasIntrinsicSize,
  validateIconFile,
  type TableIcon
} from "../../../services/adminTableIcons";
import { resolveIconUrl } from "../../../utilities/cdnUtils";
import { SHARED_ICON_FILES, sharedIconPath } from "../../../utilities/sharedIcons";

interface IconPickerDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called with the icon's CDN-relative `path` — never its absolute url. */
  onSelect: (path: string) => void;
  /** Currently selected path, highlighted in the grid. */
  value?: string;
  orgId?: number | null;
}

/** The shared library, shipped with the app (see `sharedIcons.ts`), in the picker's icon shape. */
const SHARED_ICONS: TableIcon[] = SHARED_ICON_FILES.map((file) => {
  const path = sharedIconPath(file);
  return { path, url: resolveIconUrl(path), filename: file, size: 0, shared: true };
});

/**
 * Choose a table-styling icon: upload a new one, or pick from the org's library.
 *
 * Selecting stores the icon's `path` (`public-icons/org_{id}/…` for an upload,
 * `icons/imw/…` for a shared icon), not its `url` — the url is
 * environment-specific and the SLD exporter only embeds relative paths. The
 * shared library ships with the app, so it is listed from there rather than
 * fetched from the private media bucket; it is visible to every org and cannot
 * be deleted. Besides it, an org only ever sees its own uploads.
 *
 * Upload and delete write the org's shared icon library, so they need
 * `manage_configuration` and are not offered without it. Someone styling with
 * `manage_styling` alone may still be refused the LIST as well — the backend gates
 * all three verbs on `manage_configuration` today — which is reported as such
 * rather than as an empty library.
 */
const IconPickerDialog: React.FC<IconPickerDialogProps> = ({ open, onClose, onSelect, value, orgId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { canManageConfiguration } = useAuth();
  const { data, isPending, error: listError } = useTableIcons(orgId, open);
  const listForbidden = (listError as any)?.status === 403;
  const upload = useUploadTableIcon(orgId);
  const remove = useDeleteTableIcon(orgId);

  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<{ path: string; tables: string[]; datasets: number[] } | null>(null);

  const onDrop = useCallback(
    async (files: File[]) => {
      const file = files[0];
      if (!file) return;
      setError(null);
      setConflict(null);

      // Fail fast on what we can see locally; the server re-checks everything.
      const localError = validateIconFile(file);
      if (localError) {
        setError(localError);
        return;
      }
      if (file.name.toLowerCase().endsWith(".svg")) {
        const markup = await file.text();
        if (!svgHasIntrinsicSize(markup)) {
          setError("This SVG has no width/height on its root element, so the map cannot render it. Add them, or use a PNG.");
          return;
        }
      }

      try {
        const icon = await upload.mutateAsync(file);
        // A deduplicated upload returns the pre-existing entry — same outcome.
        enqueueSnackbar(icon.deduplicated ? `"${icon.filename}" was already in the library` : `Uploaded "${icon.filename}"`, {
          variant: "success"
        });
        onSelect(icon.path);
        onClose();
      } catch (e) {
        setError(iconErrorMessage(e, "Upload failed"));
      }
    },
    [upload, enqueueSnackbar, onSelect, onClose]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/png": [], "image/jpeg": [], "image/webp": [], "image/svg+xml": [] },
    multiple: false
  });

  const handleDelete = async (icon: TableIcon) => {
    setError(null);
    setConflict(null);
    try {
      await remove.mutateAsync(icon.path);
      enqueueSnackbar(`Deleted "${icon.filename}"`, { variant: "success" });
    } catch (e) {
      const refs = iconConflictReferences(e);
      if (refs) {
        setConflict({ path: icon.path, tables: refs.tables ?? [], datasets: refs.spatial_datasets ?? [] });
        return;
      }
      setError(iconErrorMessage(e, "Could not delete the icon"));
    }
  };

  // The API also lists the bucket's copy of the shared library; the shipped one replaces it.
  const own = (data?.results ?? []).filter((i) => !i.shared);

  const grid = (icons: TableIcon[], deletable: boolean) => (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(104px, 1fr))", gap: "20px" }}>
      {icons.map((icon) => {
        const selected = icon.path === value;
        return (
          <Box
            key={icon.path}
            sx={{
              position: "relative",
              p: "8px",
              borderRadius: "10px",
              border: "2px solid",
              borderColor: selected ? "primary.main" : "divider",
              cursor: "pointer",
              "&:hover": { borderColor: "primary.light" }
            }}
            onClick={() => {
              onSelect(icon.path);
              onClose();
            }}
            role="button"
            aria-label={`Use ${icon.filename}`}
          >
            {selected && (
              <CheckCircleIcon color="primary" fontSize="small" sx={{ position: "absolute", top: 2, right: 2, bgcolor: "background.paper", borderRadius: "50%" }} />
            )}
            <Box sx={{ height: 56, display: "grid", placeItems: "center" }}>
              <Box component="img" src={icon.url} alt="" sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
            </Box>
            <Tooltip title={icon.filename}>
              <Typography variant="caption" noWrap sx={{ display: "block", textAlign: "center", color: "text.secondary" }}>
                {icon.filename}
              </Typography>
            </Tooltip>
            {deletable && canManageConfiguration && (
              <Tooltip title="Delete icon">
                <IconButton
                  size="small"
                  aria-label={`Delete ${icon.filename}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(icon);
                  }}
                  sx={{ position: "absolute", bottom: 0, right: 0 }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Choose an icon</DialogTitle>
      <DialogContent dividers>
        <Stack spacing="20px">
          {canManageConfiguration && (
            <Box
              {...getRootProps()}
              sx={{
                p: "24px",
                borderRadius: "12px",
                border: "1px dashed",
                borderColor: isDragActive ? "primary.main" : "divider",
                bgcolor: isDragActive ? "action.hover" : "background.paper",
                textAlign: "center",
                cursor: "pointer"
              }}
            >
              <input {...getInputProps()} />
              {upload.isPending ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  <CloudUploadOutlinedIcon sx={{ color: "text.disabled", fontSize: 32 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Drop an icon here, or click to choose a file
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    PNG works best on the map. JPG, WEBP and SVG are also accepted, up to 1 MB.
                  </Typography>
                </>
              )}
            </Box>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {conflict && (
            <Alert severity="warning">
              <AlertTitle>That icon is still in use</AlertTitle>
              <Typography variant="body2">
                Remove it from the styling of{" "}
                {conflict.tables.length > 0 && <strong>{conflict.tables.join(", ")}</strong>}
                {conflict.tables.length > 0 && conflict.datasets.length > 0 && " and "}
                {conflict.datasets.length > 0 && <strong>spatial dataset {conflict.datasets.join(", ")}</strong>} before
                deleting it.
              </Typography>
            </Alert>
          )}

          {listError ? (
            <Alert severity={listForbidden ? "info" : "error"}>
              {listForbidden
                ? "Browsing your organisation's own icons needs permission to manage its configuration. Ask an administrator, or use the shared library below."
                : iconErrorMessage(listError, "Could not load the icon library")}
            </Alert>
          ) : isPending ? (
            <Stack alignItems="center" sx={{ py: "24px" }}>
              <CircularProgress size={24} />
            </Stack>
          ) : (
            <Box>
              <Stack direction="row" spacing="20px" sx={{ alignItems: "center", mb: "12px" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  This organisation's icons
                </Typography>
                <Chip size="small" variant="outlined" label={own.length} />
              </Stack>
              {own.length === 0 ? (
                <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                  {canManageConfiguration
                    ? "Nothing uploaded yet. Icons you add here are private to this organisation."
                    : "Nothing uploaded yet."}
                </Typography>
              ) : (
                grid(own, true)
              )}
            </Box>
          )}

          <Box>
            <Stack direction="row" spacing="20px" sx={{ alignItems: "center", mb: "12px" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Shared library
              </Typography>
              <Chip size="small" variant="outlined" label={SHARED_ICONS.length} />
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Available to every organisation, read-only
              </Typography>
            </Stack>
            {grid(SHARED_ICONS, false)}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: "24px", py: "16px" }}>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default IconPickerDialog;
